// React Query hooks for the message-templates pages.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTemplate,
  listTemplates,
  updateTemplate,
  type ListTemplatesFilters,
} from '../../../../api/templates';
import { queryKeys } from '../../../../api/queryKeys';
import { useToast } from '../../../../hooks/useToast';
import type {
  TemplateInfo,
  TemplateListResponse,
  UpdateTemplateInput,
} from '../../../../types/api';

interface ToggleVars {
  uuid: string;
  is_active: boolean;
}

interface ToggleContext {
  previous: Array<[ReadonlyArray<unknown>, TemplateListResponse | undefined]>;
}

export function useTemplatesList(filters: ListTemplatesFilters) {
  return useQuery<TemplateListResponse>({
    queryKey: queryKeys.templates.list(filters as unknown as Record<string, unknown>),
    queryFn: () => listTemplates(filters),
    staleTime: 60_000,
  });
}

export function useTemplateDetail(uuid: string | undefined) {
  return useQuery<TemplateInfo>({
    queryKey: uuid ? queryKeys.templates.detail(uuid) : ['templates', 'detail', 'none'],
    queryFn: () => getTemplate(uuid as string),
    enabled: Boolean(uuid),
    staleTime: 60_000,
  });
}

export function useUpdateTemplate(uuid: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpdateTemplateInput) => updateTemplate(uuid, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.templates.detail(uuid) });
      void qc.invalidateQueries({ queryKey: queryKeys.templates.all });
      toast.success('Template updated.');
    },
    onError: () => {
      toast.error('Failed to update template.');
    },
  });
}

/**
 * Toggle a template's `is_active` flag from the list page. Updates every
 * cached list query optimistically so the switch flips immediately;
 * rolls back to the snapshot on error.
 */
export function useToggleTemplateActive() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<TemplateInfo, Error, ToggleVars, ToggleContext>({
    mutationFn: ({ uuid, is_active }) =>
      updateTemplate(uuid, { is_active }),
    onMutate: async ({ uuid, is_active }) => {
      await qc.cancelQueries({ queryKey: queryKeys.templates.all });
      const previous = qc.getQueriesData<TemplateListResponse>({
        queryKey: queryKeys.templates.all,
      });
      qc.setQueriesData<TemplateListResponse>(
        { queryKey: queryKeys.templates.all },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            templates: old.templates.map((t) =>
              t.uuid === uuid ? { ...t, is_active } : t,
            ),
          };
        },
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        for (const [key, data] of ctx.previous) {
          qc.setQueryData(key, data);
        }
      }
      toast.error('Could not update template. Reverted.');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
}
